/**
 * AI-ассистент — выдвижная панель (Drawer) с чат-интерфейсом.
 *
 * Позволяет пользователю задавать вопросы по безопасности полётов,
 * статистике и аналитике. На текущем этапе используется backend-заглушка
 * с keyword-matching; интеграция с LLM будет добавлена позднее.
 */
import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import {
  Box,
  Chip,
  Drawer,
  Fab,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Sparkles, Send, Trash2, X } from 'lucide-react';
import DOMPurify from 'dompurify';
import { askAI } from '@/api/endpoints';

// Whitelist HTML тэгов и атрибутов для рендеринга markdown-ответа AI.
// Никакого `<script>`, `<iframe>`, on-обработчиков, javascript: URL.
// Whitelist HTML тегов и атрибутов. Cast as Parameters<typeof DOMPurify.sanitize>[1]
// чтобы избежать рассинхрона типов между dompurify@3 и @types/dompurify.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'b', 'i',
    'ul', 'ol', 'li', 'code', 'pre',
    'h1', 'h2', 'h3', 'h4',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'a', 'blockquote', 'hr',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
} as Parameters<typeof DOMPurify.sanitize>[1];

// ---------------------------------------------------------------------------
// Типы
// ---------------------------------------------------------------------------

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  sources?: string[];
}

// ---------------------------------------------------------------------------
// Быстрые действия (quick-action chips)
// ---------------------------------------------------------------------------

const QUICK_ACTIONS = [
  'Анализ безопасности за месяц',
  'Топ-5 рисков',
  'Статистика полётов',
  'Рекомендации по улучшению',
] as const;

// ---------------------------------------------------------------------------
// Анимация набора текста — 3 прыгающих точки
// ---------------------------------------------------------------------------

const TypingIndicator: React.FC = () => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, py: 1, px: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: theme.palette.text.secondary,
            animation: 'aiTypingBounce 1.4s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes aiTypingBounce': {
              '0%, 80%, 100%': { transform: 'translateY(0)' },
              '40%': { transform: 'translateY(-8px)' },
            },
          }}
        />
      ))}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Пузырёк сообщения
// ---------------------------------------------------------------------------

interface MessageBubbleProps {
  message: ChatMessage;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const theme = useTheme();
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: '88%',
          px: 2,
          py: 1.5,
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          bgcolor: isUser
            ? theme.palette.primary.main
            : theme.palette.background.paper,
          color: isUser
            ? theme.palette.primary.contrastText
            : theme.palette.text.primary,
          border: isUser
            ? 'none'
            : `1px solid ${theme.palette.divider}`,
          boxShadow: isUser
            ? '0 2px 8px rgba(59,130,246,0.3)'
            : '0 1px 4px rgba(0,0,0,0.08)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: 14,
          lineHeight: 1.6,
          '& h2': {
            fontSize: 16,
            fontWeight: 600,
            mt: 0,
            mb: 1,
          },
          '& h3': {
            fontSize: 14,
            fontWeight: 600,
            mt: 1,
            mb: 0.5,
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            my: 1,
            fontSize: 13,
          },
          '& th, & td': {
            border: `1px solid ${theme.palette.divider}`,
            px: 1,
            py: 0.5,
            textAlign: 'left',
          },
          '& th': {
            fontWeight: 600,
            bgcolor: theme.palette.action.hover,
          },
          '& blockquote': {
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            pl: 1.5,
            ml: 0,
            opacity: 0.85,
            fontStyle: 'italic',
          },
          '& strong': {
            fontWeight: 600,
          },
          '& ul, & ol': {
            pl: 2.5,
            my: 0.5,
          },
        }}
        dangerouslySetInnerHTML={
          isUser
            ? undefined
            : { __html: DOMPurify.sanitize(markdownToHtml(message.text), SANITIZE_CONFIG) }
        }
      >
        {isUser ? message.text : undefined}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Минимальный markdown → HTML конвертер
// ---------------------------------------------------------------------------

function markdownToHtml(md: string): string {
  let html = md
    // Экранируем HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Заголовки
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');

  // Жирный текст
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Курсив
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Цитаты
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');

  // Таблицы: находим блоки таблиц и конвертируем
  html = html.replace(
    /^(\|.+\|)\n(\|[-| :]+\|)\n((?:\|.+\|\n?)*)/gm,
    (_match, headerRow: string, _separator: string, bodyRows: string) => {
      const headers = headerRow
        .split('|')
        .filter((c: string) => c.trim())
        .map((c: string) => `<th>${c.trim()}</th>`)
        .join('');
      const rows = bodyRows
        .trim()
        .split('\n')
        .map((row: string) => {
          const cells = row
            .split('|')
            .filter((c: string) => c.trim())
            .map((c: string) => `<td>${c.trim()}</td>`)
            .join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      return `<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
    },
  );

  // Списки (простой вариант — нумерованные)
  html = html.replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, (block) => `<ol>${block}</ol>`);

  // Маркированные списки
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, (block) => {
    if (block.includes('<ol>')) return block;
    return `<ul>${block}</ul>`;
  });

  // Переносы строк
  html = html.replace(/\n\n/g, '<br/><br/>');
  html = html.replace(/\n/g, '<br/>');

  return html;
}

// ---------------------------------------------------------------------------
// Компонент AI-ассистента
// ---------------------------------------------------------------------------

const AIAssistant: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextIdRef = useRef(1);

  // Автоскролл к последнему сообщению
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Фокус на поле ввода при открытии
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const handleSend = useCallback(
    async (text?: string) => {
      const question = (text ?? input).trim();
      if (!question || loading) return;

      const userMsg: ChatMessage = {
        id: nextIdRef.current++,
        role: 'user',
        text: question,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setLoading(true);

      try {
        const data = await askAI(question);
        const aiMsg: ChatMessage = {
          id: nextIdRef.current++,
          role: 'assistant',
          text: data.answer,
          sources: data.sources,
        };
        setMessages((prev) => [...prev, aiMsg]);
      } catch {
        const errMsg: ChatMessage = {
          id: nextIdRef.current++,
          role: 'assistant',
          text: 'Не удалось получить ответ. Проверьте подключение к серверу и попробуйте снова.',
        };
        setMessages((prev) => [...prev, errMsg]);
      } finally {
        setLoading(false);
      }
    },
    [input, loading],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessages([]);
    nextIdRef.current = 1;
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {/* FAB — кнопка вызова ассистента */}
      <Tooltip title="AI Ассистент (beta)" placement="top">
        <Fab
          size="medium"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 28,
            right: 28,
            zIndex: theme.zIndex.speedDial,
            background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
            color: '#fff',
            boxShadow: '0 4px 20px rgba(99,102,241,0.4)',
            animation: 'aiGlow 2s ease-in-out infinite alternate',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
              boxShadow: '0 6px 28px rgba(99,102,241,0.55)',
            },
            '@keyframes aiGlow': {
              '0%': { boxShadow: '0 4px 20px rgba(99,102,241,0.35)' },
              '100%': { boxShadow: '0 4px 28px rgba(139,92,246,0.55)' },
            },
          }}
        >
          <Sparkles size={22} />
        </Fab>
      </Tooltip>

      {/* Drawer — панель чата */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 420,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.default,
          },
        }}
      >
        {/* Заголовок */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
            color: '#fff',
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Sparkles size={22} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                AI Ассистент
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                beta — аналитика безопасности
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Очистить чат">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}
              >
                <Trash2 size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Закрыть">
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}
              >
                <X size={18} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Область сообщений */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {messages.length === 0 && !loading && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                opacity: 0.6,
                gap: 2,
                px: 3,
              }}
            >
              <Sparkles size={40} />
              <Typography variant="body2">
                Задайте вопрос по безопасности полётов, статистике или аналитике.
                Выберите один из быстрых запросов ниже или напишите свой.
              </Typography>
            </Box>
          )}

          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: '16px 16px 16px 4px',
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <TypingIndicator />
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Quick action chips */}
        <Box
          sx={{
            px: 2,
            pb: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {QUICK_ACTIONS.map((action) => (
            <Chip
              key={action}
              label={action}
              size="small"
              variant="outlined"
              onClick={() => handleSend(action)}
              disabled={loading}
              sx={{
                fontSize: 12,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: `${theme.palette.primary.main}14`,
                },
              }}
            />
          ))}
        </Box>

        {/* Поле ввода */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
            flexShrink: 0,
            bgcolor: theme.palette.background.paper,
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="Задайте вопрос..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
              },
            }}
          />
          <IconButton
            onClick={() => handleSend()}
            disabled={!input.trim() || loading}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: '#fff',
              width: 40,
              height: 40,
              borderRadius: '12px',
              flexShrink: 0,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
              '&.Mui-disabled': {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            <Send size={18} />
          </IconButton>
        </Box>
      </Drawer>
    </>
  );
};

export default AIAssistant;
